import { lazy, Suspense, useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFeatureIsOn, useGrowthBook } from "@growthbook/growthbook-react";
import { useLocation } from "react-router";
import { AppState } from "@Store/index";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import {
  setOpenAmberCopilot,
  setIsChatbotOpen,
} from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";
import wrapperHOC from "@Utils/wrapperHOC";
import { useIsNewNativeApp } from "hooks/useIsApp";
import { initializeStorage } from "./utils/chatbotStorage";

const Chatbot = lazy(() => import("@Components/Chatbot"));
const ChatbotButton = lazy(() => import("@Components/Chatbot/ChatbotButton"));

interface AmberCopilotWrapperProps {
  showFloatingButtonForNewNativeApp?: boolean;
}

const AmberCopilotWrapper = ({
  showFloatingButtonForNewNativeApp = false,
}: AmberCopilotWrapperProps) => {
  const [showButton, setShowButton] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const dispatch = useDispatch();
  const growthbook = useGrowthBook();
  const location = useLocation();
  const prevPathnameRef = useRef<string | null>(null);

  const { isChatbotOpen, leadFormPage, openLeadFormModal, openingSource } = useSelector(
    ({ chatBotReducer, leadFormModal }: AppState) => ({
      isChatbotOpen: chatBotReducer.isChatbotOpen,
      leadFormPage: leadFormModal.leadFormPage,
      openLeadFormModal: leadFormModal.openLeadFormModal,
      openingSource: leadFormModal.openingSource,
    }),
  );

  const isAmberCopilotEnabled = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.AMBER_COPILOT);
  // New native tab bars open `/ai-chat`; pages can opt back into the floating launcher.
  const hasNativeChatButton = useIsNewNativeApp();
  const isSuccessPage =
    openLeadFormModal &&
    ((leadFormPage === 2 && openingSource !== "virtual_tour_enquiry") ||
      (leadFormPage === 3 && openingSource === "virtual_tour_enquiry"));

  const isPaymentPage =
    typeof window !== "undefined" && window.location.pathname.includes("/payments");

  const isExcludedPage =
    location.pathname.includes("/scholarship") ||
    location.pathname.includes("/exams") ||
    location.pathname === "/contact";

  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeStorage();

        if (growthbook) {
          const checkReady = () =>
            new Promise<void>((resolve) => {
              if (growthbook.ready) {
                resolve();
              } else {
                const timeout = setTimeout(() => {
                  resolve();
                }, 2000);

                const checkInterval = setInterval(() => {
                  if (growthbook.ready) {
                    clearInterval(checkInterval);
                    clearTimeout(timeout);
                    resolve();
                  }
                }, 100);
              }
            });

          await checkReady();
        }

        setIsReady(true);
      } catch (error) {
        console.error("Error initializing AmberCopilot:", error);
        setIsReady(true);
      }
    };

    initialize();
  }, [growthbook]);

  const handleChatbotButtonClick = useCallback(() => {
    setShowButton(false);
    dispatch(setOpenAmberCopilot(true));
  }, [dispatch]);

  useEffect(() => {
    setShowButton(!isChatbotOpen);
  }, [isChatbotOpen]);

  useEffect(() => {
    const currentPathname = location.pathname;

    if (prevPathnameRef.current === null) {
      prevPathnameRef.current = currentPathname;
      return;
    }

    if (prevPathnameRef.current !== currentPathname && isChatbotOpen) {
      dispatch(setIsChatbotOpen(false));
      dispatch(setOpenAmberCopilot(false));
    }

    prevPathnameRef.current = currentPathname;
  }, [location.pathname, isChatbotOpen, dispatch]);

  useEffect(
    () => () => {
      if (isChatbotOpen) {
        dispatch(setIsChatbotOpen(false));
        dispatch(setOpenAmberCopilot(false));
      }
    },
    [isChatbotOpen, dispatch],
  );

  if (!isReady || !isAmberCopilotEnabled) {
    return null;
  }

  return (
    <>
      {showButton &&
        !isSuccessPage &&
        !isPaymentPage &&
        !isExcludedPage &&
        (!hasNativeChatButton || showFloatingButtonForNewNativeApp) && (
          <Suspense fallback={null}>
            <ChatbotButton onClick={handleChatbotButtonClick} />
          </Suspense>
        )}
      <Suspense fallback={null}>
        <Chatbot />
      </Suspense>
    </>
  );
};

export default wrapperHOC(AmberCopilotWrapper, {
  componentName: "AmberCopilotWrapper-Chatbot",
  showForChina: false,
  showForWhiteLable: false,
});
